import React, { useCallback } from "react";

import type { ServerDataSuccess } from "./route";

import {
  Image,
  Badge,
  Zoomable,
  useModal,
  IconVideo,
  ImageModal,
  VideoMarketing,
  CarouselSection,
  ProductImageGrid,
  ProductImageCarousel,
  ProductImageGridSection,
  HowToStyleAnchorButton,
} from "@bared/ui";

import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

export type ProductImageGalleryProps = Pick<
  ServerDataSuccess,
  "productMedia" | "badge"
> & {
  /** Unique ID for the gallery modal */
  modalId: string;
  howToStyle?: boolean;
};

export function ProductImageGallery({
  modalId,
  ...props
}: ProductImageGalleryProps) {
  return (
    <>
      <div className="md:hidden">
        <ProductImageGalleryCarousel modalId={modalId} {...props} />
      </div>
      <div className="hidden md:block">
        <ProductImageGalleryGrid modalId={modalId} {...props} />
      </div>
      <ProductImageGalleryModal modalId={modalId} {...props} />
    </>
  );
}

function ProductImageGalleryGrid({
  modalId,
  badge,
  productMedia,
}: ProductImageGalleryProps) {
  const { showModal } = useModal();
  return (
    <ProductImageGrid>
      {productMedia.map((media, index) => (
        <ProductImageGridSection
          index={index}
          key={`image-${index}`}
          className="relative aspect-11-12 bg-white"
          onClick={() => showModal(modalId, { initialIndex: index })}
        >
          {0 === index && badge && (
            <Badge
              {...badge}
              size="small"
              className="absolute left-mini top-mini md:left-maxi md:top-maxi"
            />
          )}
          {"image" === media.type && (
            <Image
              {...media}
              sizes="33vw"
              loading="lazy"
              aspectRatio="11/12"
              fetchPriority={index < 2 ? "high" : "auto"}
              className="bg-canvas"
            />
          )}
          {"video" === media.type && (
            <>
              <Image
                {...media.coverImage}
                sizes="33vw"
                loading="lazy"
                aspectRatio="11/12"
                fetchPriority={index < 2 ? "high" : "auto"}
                className="top-0 absolute bg-canvas"
              />
              <div className="flex items-center justify-center">
                <IconVideo className="transition-presentation duration-slow z-10 block w-[40px] rounded-md bg-primary bg-opacity-50 text-primary text-white hover:bg-opacity-100" />
              </div>
            </>
          )}
        </ProductImageGridSection>
      ))}
    </ProductImageGrid>
  );
}

function ProductImageGalleryCarousel({
  modalId,
  badge,
  productMedia,
  howToStyle,
}: ProductImageGalleryProps) {
  const { showModal } = useModal();
  const googleTagManager = useGoogleTagManager();

  const onClickHowToStyle = useCallback(() => {
    const node = document.getElementById("how_to_style_section");
    if (!node) return;

    node.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });

    googleTagManager.push({
      event: "Button Clicked",
      properties: {
        label: "How to style",
      },
    });
  }, [googleTagManager]);

  return (
    <div className="relative">
      <ProductImageCarousel>
        {productMedia.map((media, index) => (
          <CarouselSection
            index={index}
            key={`image-${index}`}
            className="relative aspect-11-12 bg-white"
            onClick={() => showModal(modalId, { initialIndex: index })}
          >
            {0 === index && badge && (
              <Badge
                {...badge}
                size="small"
                className="absolute left-mini top-mini md:left-maxi md:top-maxi"
              />
            )}
            {"image" === media.type && (
              <Image
                {...media}
                sizes="100vw"
                loading="eager"
                aspectRatio="11/12"
                className="bg-canvas"
              />
            )}
            {"video" === media.type && (
              <>
                <Image
                  {...media.coverImage}
                  sizes="100vw"
                  loading="eager"
                  aspectRatio="11/12"
                  className="absolute bg-canvas"
                />
                <div className="flex w-full items-center justify-center">
                  <IconVideo className="transition-presentation duration-slow z-10 block w-[40px] rounded-md bg-primary bg-opacity-50 text-primary text-white hover:bg-opacity-100" />
                </div>
              </>
            )}
          </CarouselSection>
        ))}
      </ProductImageCarousel>
      {howToStyle && <HowToStyleAnchorButton onClick={onClickHowToStyle} />}
    </div>
  );
}

function ProductImageGalleryModal({
  modalId,
  productMedia,
}: ProductImageGalleryProps) {
  return (
    <ImageModal id={modalId} ariaLabel="Product images">
      {productMedia.map((media, index) => (
        <CarouselSection
          index={index}
          key={`image-${index}`}
          className="landscape:aspect-11-12"
        >
          {"image" === media.type && (
            <Zoomable>
              <Image
                {...media}
                sizes="100vw"
                loading="eager"
                aspectRatio="11/12"
                className="bg-canvas"
              />
            </Zoomable>
          )}
          {"video" === media.type && (
            <VideoMarketing
              url={media.videoUrl}
              fallback={
                <Image
                  {...media.coverImage}
                  sizes="100vw"
                  loading="eager"
                  aspectRatio="11/12"
                />
              }
            />
          )}
        </CarouselSection>
      ))}
    </ImageModal>
  );
}
